/* @jsx h */
import { defineComponent, h } from "vue";
import type { ReservationSettingsUpdateHandler } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { RichTextEditor } from "@lemmonhq/uikit/rich-text-editor";

export interface CustomerMessagingProps {
  reservationNote: string;
  confirmationMessage: string;
  cancellationPolicy: string;
  rejectionMessage: string;
  hideDurationToGuests: boolean;
  onUpdate: ReservationSettingsUpdateHandler;
}

export const CustomerMessaging = defineComponent<CustomerMessagingProps>({
  name: "CustomerMessaging",
  props: [
    "reservationNote",
    "confirmationMessage",
    "cancellationPolicy",
    "rejectionMessage",
    "hideDurationToGuests",
    "onUpdate",
  ],
  setup(props) {
    const { t } = useLocalization();

    const field = (
      label: string,
      hint: string,
      value: string,
      key:
        | "reservations_note"
        | "reservations_confirmation_message"
        | "reservations_cancellation_policy"
        | "reservations_rejection_message",
    ) => (
      <div class="bg-base-200 p-2 border rounded">
        <label class="block text-sm font-medium  mb-2">{label}</label>
        <RichTextEditor
          value={value}
          onChange={(html) => props.onUpdate(key, html)}
        />
        <p class="text-xs  mt-1">{hint}</p>
      </div>
    );

    return () => (
      <div class="space-y-4">
        <div class="form-control">
          <label class="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              class="toggle toggle-primary"
              checked={props.hideDurationToGuests}
              onChange={(e) =>
                props.onUpdate(
                  "reservations_hide_duration_to_guests",
                  (e.target as HTMLInputElement).checked,
                )
              }
            />
            <div>
              <span class="label-text  font-medium">
                {t("settings.reservations.hide_duration_to_guests")}
              </span>
              <p class="text-sm  mt-1">
                {t("settings.reservations.hide_duration_to_guests_desc")}
              </p>
            </div>
          </label>
        </div>

        {field(
          "Reservation Page Note",
          "Displayed prominently when customers make a reservation",
          props.reservationNote,
          "reservations_note",
        )}
        {field(
          "Confirmation Message",
          "Shown after a successful reservation and in the confirmation email",
          props.confirmationMessage,
          "reservations_confirmation_message",
        )}
        {field(
          "Cancellation Policy",
          "Let customers know your cancellation terms",
          props.cancellationPolicy,
          "reservations_cancellation_policy",
        )}
        {field(
          "Rejection Message",
          "Sent to the guest when a reservation request is declined",
          props.rejectionMessage,
          "reservations_rejection_message",
        )}
      </div>
    );
  },
});
